import { not, or } from 'tio-ui/utilities';
import ReCheckbox from 'tio-reimagined/components/checkbox';
import ReSwitch from 'tio-reimagined/components/switch';
import { PhInfo, PhX } from 'ember-phosphor-icons';
import { on } from '@ember/modifier';
import { array, fn } from '@ember/helper';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import ApplicationFilter from './filters/application.gts';
import Component from '@glimmer/component';
import EmberFlatpickr from 'ember-flatpickr/components/ember-flatpickr';
import PaymentFilter from './filters/payments.gts';
import ReSelect from 'tio-reimagined/components/select';
import type CompanyModel from 'tio-common/models/company';
import type EmployeeModel from 'tio-common/models/employee';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import type TasProgramTemplateModel from 'tio-common/models/tas-program-template';
import type { ApplicationFilterSignature, PaymentFilterSignature } from './types';

interface AdvancedProgramFiltersSignature {
  Args: ApplicationFilterSignature &
    PaymentFilterSignature & {
      company?: CompanyModel;
      programTemplateId?: string;
      programCreatedAtStart?: string;
      programCreatedAtEnd?: string;
      excludeNonVoucherPrograms?: boolean;
      courseCashProgramsOnly?: boolean;
      includeCourses?: boolean;
      coursesBeginDate?: string;
      coursesEndDate?: string;
      approverId?: string;
      employeeId?: string;
      terminatedEmployees?: boolean;
      excludeTermVerified?: boolean;
      includeInvoiceDate?: boolean;
      showIndividualApplications?: boolean;
      onFilterUpdate: (filterUpdates: Record<string, unknown>) => void;
      monthOffset?: number;
      isAdminContext?: boolean;
      onCompanyChange?: (company?: CompanyModel) => void;
    };
  Element: HTMLDivElement;
}

export class AdvancedProgramFilters extends Component<AdvancedProgramFiltersSignature> {
  @service declare store: Store;
  @service declare intl: IntlService;

  get companyId() {
    return this.args.company?.id;
  }

  /***************************/
  /* Program Template Data   */
  /***************************/

  programsData = trackedFunction(this, async (): Promise<TasProgramTemplateModel[]> => {
    if (!this.companyId) {
      return [];
    }
    return await this.store.query<TasProgramTemplateModel>('tas-program-template', {
      filter: {
        company: this.companyId,
      },
    });
  });

  get programOptions(): TasProgramTemplateModel[] {
    return this.programsData.value || [];
  }

  get programSelectOptions() {
    return this.programOptions.map((program: TasProgramTemplateModel) => ({
      label: program.programName,
      key: program.id,
    }));
  }

  /***************************/
  /* Partner Selection       */
  /***************************/

  companiesData = trackedFunction(this, async (): Promise<CompanyModel[]> => {
    return this.store.query<CompanyModel>('company', {
      filter: { 'is-active': 1 },
      page: { limit: 400 },
    }) as Promise<CompanyModel[]>;
  });

  get companyItems(): CompanyModel[] {
    return this.companiesData.value || [];
  }

  companyDidChange = (key: string | null) => {
    const company = key ? this.companyItems.find((c) => c.id === key) : undefined;
    if (this.args.onCompanyChange) {
      this.args.onCompanyChange(company);
    }
  };

  /***************************/
  /* Program Template Filters */
  /***************************/

  programTemplateDidChange = (programTemplateId: string | null) => {
    this.args.onFilterUpdate({ programTemplateId });
  };

  programCreatedAtStartDidChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString = date ? date.toISOString().split('T')[0] : '';
    this.args.onFilterUpdate({ programCreatedAtStart: dateString });
  };

  programCreatedAtEndDidChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString = date ? date.toISOString().split('T')[0] : '';
    this.args.onFilterUpdate({ programCreatedAtEnd: dateString });
  };

  /***************************/
  /* Course Filters          */
  /***************************/

  includeCoursesDidChange = (state: boolean) => {
    this.args.onFilterUpdate({
      includeCourses: state,
      coursesBeginDate: '',
      coursesEndDate: '',
    });
  };

  coursesBeginDateDidChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString = date ? date.toISOString().split('T')[0] : '';
    this.args.onFilterUpdate({ coursesBeginDate: dateString });
  };

  coursesEndDateDidChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString = date ? date.toISOString().split('T')[0] : '';
    this.args.onFilterUpdate({ coursesEndDate: dateString });
  };

  /***************************/
  /* Employee Filters        */
  /***************************/

  @tracked employeeSearchTerm = '';

  employeesData = trackedFunction(this, async () => {
    if (!this.companyId || !this.employeeSearchTerm) return [];
    const results = await this.store.query<EmployeeModel>('employee', {
      filter: {
        search: this.employeeSearchTerm,
        company: this.companyId,
      },
    });
    return results
      .filter((e): e is EmployeeModel & { id: string } => Boolean(e.id))
      .map((e) => ({ key: e.id, label: e.fullName }));
  });

  selectedEmployeeRecord = trackedFunction(this, async () => {
    const id = this.args.employeeId;
    if (!id) return null;
    try {
      return await this.store.findRecord<EmployeeModel>('employee', id);
    } catch {
      return null;
    }
  });

  get employeeItems() {
    const searchResults = this.employeesData.value || [];
    const selected = this.selectedEmployeeRecord.value;
    if (selected?.id && !searchResults.find((r) => r.key === selected.id)) {
      return [{ key: selected.id, label: selected.fullName }, ...searchResults];
    }
    return searchResults;
  }

  onEmployeeSearch = (query: string) => {
    this.employeeSearchTerm = query;
  };

  selectedEmployeeDidChange = (id: string | null) => {
    this.args.onFilterUpdate({ employeeId: id });
  };

  terminatedEmployeesDidChange = (state: boolean) => {
    this.args.onFilterUpdate({ terminatedEmployees: state });
  };

  excludeTermVerifiedDidChange = (state: boolean) => {
    this.args.onFilterUpdate({ excludeTermVerified: state });
  };

  /***************************/
  /* Approver Filters        */
  /***************************/

  @tracked approver?: EmployeeModel;

  get approverOptions() {
    return (
      this.approversData.value?.map((e) => ({
        label: e.fullName,
        key: e.id,
      })) || []
    );
  }

  approversData = trackedFunction(this, async () => {
    return this.store.adapterFor('company').getTasApprovers(this.companyId) as EmployeeModel[];
  });

  selectedApproverDidChange = (id: string | null) => {
    this.args.onFilterUpdate({ approverId: id });
  };

  /***************************/
  /* Invoice Filters         */
  /***************************/

  includeInvoiceDateDidChange = (state: boolean) => {
    this.args.onFilterUpdate({ includeInvoiceDate: state });
  };

  /***************************/
  /* Program Filters         */
  /***************************/

  excludeNonVoucherProgramsDidChange = (value: boolean) => {
    this.args.onFilterUpdate({
      excludeNonVoucherPrograms: value,
    });
  };

  courseCashProgramsOnlyDidChange = (value: boolean) => {
    this.args.onFilterUpdate({
      courseCashProgramsOnly: value,
    });
  };

  <template>
    <div class="flex flex-col gap-y-10 text-sm" ...attributes>
      {{! PARTNER SECTION - Admin Only }}
      {{#if @isAdminContext}}
        <fieldset>
          <div class="flex flex-col gap-y-6">
            <div class="flex flex-col gap-y-3">
              <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500">Partner</h3>
              <div
                class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4"
              >
                <div class="flex items-start gap-2">
                  <PhInfo
                    @size="20"
                    class="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
                  />
                  <div>
                    <h4 class="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                      Testing Context
                    </h4>
                    <p class="text-xs text-blue-700 dark:text-blue-300">
                      Select a partner to preview their data and access partner-specific filters.
                      This selection is NOT saved with the report—it's for testing only.
                    </p>
                  </div>
                </div>
              </div>
              <ReSelect
                @label="Filter by Partner"
                @items={{this.companyItems}}
                @selected={{@company.id}}
                @onChange={{this.companyDidChange}}
                @searchable={{true}}
                @placeholder="All Partners"
                @size="sm"
                @isLoading={{this.companiesData.isLoading}}
              />
            </div>
          </div>
        </fieldset>
      {{/if}}

      <fieldset>
        <div class="flex flex-col gap-y-6">
          {{! PROGRAMS SECTION }}
          <div class="flex flex-col gap-y-3">
            <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500">Programs</h3>
            <div class="flex flex-col gap-y-3 pb-3 border-b border-gray-200 dark:border-gray-600">
              <ReSelect
                @clearable={{true}}
                @items={{this.programSelectOptions}}
                @selected={{@programTemplateId}}
                @label={{t "common.reporting.filter_by_program"}}
                @isLoading={{this.programsData.isLoading}}
                @onChange={{this.programTemplateDidChange}}
                @size="sm"
              />
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium text-gray-900">Program Creation Date</label>
                <div class="flex items-center gap-2">
                  <span class="text-sm text-gray-700">From</span>
                  <div class="flex-1 relative">
                    {{! @glint-expect-error: return to this, follow up with dev}}
                    <EmberFlatpickr
                      @date={{or @programCreatedAtStart ""}}
                      @onChange={{this.programCreatedAtStartDidChange}}
                      @dateFormat="Y-m-d"
                      @altInput={{true}}
                      @altFormat="F j, Y"
                      placeholder="mm/dd/yyyy"
                      class="tio-form-input"
                    />
                    {{#if @programCreatedAtStart}}
                      <button
                        type="button"
                        {{on "click" (fn this.programCreatedAtStartDidChange (array))}}
                        class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label="Clear start date"
                      >
                        <PhX @size="16" />
                      </button>
                    {{/if}}
                  </div>
                  <span class="text-sm text-gray-700">To</span>
                  <div class="flex-1 relative">
                    {{! @glint-expect-error: return to this, follow up with dev}}
                    <EmberFlatpickr
                      @date={{or @programCreatedAtEnd ""}}
                      @onChange={{this.programCreatedAtEndDidChange}}
                      @dateFormat="Y-m-d"
                      @altInput={{true}}
                      @altFormat="F j, Y"
                      placeholder="mm/dd/yyyy"
                      @minDate={{or @programCreatedAtStart ""}}
                      class="tio-form-input"
                    />
                    {{#if @programCreatedAtEnd}}
                      <button
                        type="button"
                        {{on "click" (fn this.programCreatedAtEndDidChange (array))}}
                        class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label="Clear end date"
                      >
                        <PhX @size="16" />
                      </button>
                    {{/if}}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {{! APPLICATIONS SECTION }}
          <div class="flex flex-col gap-y-3">
            <h3
              class="text-xs font-semibold uppercase tracking-wide text-gray-500"
            >Applications</h3>
            <div class="flex flex-col gap-y-3 pb-3 border-b border-gray-200 dark:border-gray-600">
              <ApplicationFilter
                @showIndividualApplications={{@showIndividualApplications}}
                @applicationStates={{@applicationStates}}
                @applicationCreatedAtStart={{@applicationCreatedAtStart}}
                @applicationCreatedAtEnd={{@applicationCreatedAtEnd}}
                @applicationDateRangePreset={{@applicationDateRangePreset}}
                @onFilterUpdate={{@onFilterUpdate}}
              />
            </div>
          </div>

          {{! PAYMENT DETAILS SECTION }}
          {{#if @showIndividualApplications}}
            <div class="flex flex-col gap-y-3">
              <div class="flex flex-col gap-y-3 pb-3 border-b border-gray-200 dark:border-gray-600">
                <PaymentFilter
                  @paymentYear={{@paymentYear}}
                  @monthOffset={{@monthOffset}}
                  @includePayments={{@includePayments}}
                  @paymentStates={{@paymentStates}}
                  @onFilterUpdate={{@onFilterUpdate}}
                />
              </div>
            </div>
          {{/if}}

          {{! OTHER FILTERS }}
          <div class="flex flex-col gap-y-3 pb-3 border-b border-gray-200 dark:border-gray-600">
            <ReCheckbox
              @label="Include Invoice Dates"
              @checked={{or @includeInvoiceDate false}}
              @onChange={{this.includeInvoiceDateDidChange}}
              @size="sm"
            />
          </div>

          <div class="flex flex-col gap-y-3 pb-3 border-b border-gray-200 dark:border-gray-600">
            <ReCheckbox
              @label="Include Course Details"
              @checked={{or @includeCourses false}}
              @onChange={{this.includeCoursesDidChange}}
              @size="sm"
            />
            <div
              class="{{if @includeCourses 'max-h-dvh overflow-visible' 'max-h-0 overflow-hidden'}}
                flex flex-col gap-2 ml-3 transition-all duration-500"
            >
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-700">From</span>
                <div class="flex-1 relative">
                  {{! @glint-expect-error: return to this, follow up with dev}}
                  <EmberFlatpickr
                    @date={{or @coursesBeginDate ""}}
                    @onChange={{this.coursesBeginDateDidChange}}
                    @dateFormat="Y-m-d"
                    @altInput={{true}}
                    @altFormat="F j, Y"
                    placeholder="mm/dd/yyyy"
                    class="tio-form-input"
                  />
                  {{#if @coursesBeginDate}}
                    <button
                      type="button"
                      {{on "click" (fn this.coursesBeginDateDidChange (array))}}
                      class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Clear begin date"
                    >
                      <PhX @size="16" />
                    </button>
                  {{/if}}
                </div>
                <span class="text-sm text-gray-700">To</span>
                <div class="flex-1 relative">
                  {{! @glint-expect-error: return to this, follow up with dev}}
                  <EmberFlatpickr
                    @date={{or @coursesEndDate ""}}
                    @onChange={{this.coursesEndDateDidChange}}
                    @dateFormat="Y-m-d"
                    @altInput={{true}}
                    @altFormat="F j, Y"
                    @minDate={{or @coursesBeginDate ""}}
                    placeholder="mm/dd/yyyy"
                    class="tio-form-input"
                  />
                  {{#if @coursesEndDate}}
                    <button
                      type="button"
                      {{on "click" (fn this.coursesEndDateDidChange (array))}}
                      class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Clear end date"
                    >
                      <PhX @size="16" />
                    </button>
                  {{/if}}
                </div>
              </div>
            </div>
          </div>

          {{! APPROVERS SECTION }}
          <div class="flex flex-col gap-y-3">
            <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500">Approvers</h3>
            <div class="flex flex-col gap-y-3 pb-3 border-b border-gray-200 dark:border-gray-600">
              <ReSelect
                @label={{t "common.reporting.filter_by_approver"}}
                @items={{this.approverOptions}}
                @selected={{@approverId}}
                @onChange={{this.selectedApproverDidChange}}
                @clearable={{true}}
                @disabled={{not @showIndividualApplications}}
                @isLoading={{this.approversData.isLoading}}
                @size="sm"
              />
            </div>
          </div>

          {{! EMPLOYEES SECTION }}
          <div class="flex flex-col gap-y-3">
            <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500">Employees</h3>
            <div class="flex flex-col gap-y-3 border-b border-gray-200 dark:border-gray-600 pb-3">
              {{#if this.companyId}}
                <ReSelect
                  @label={{t "common.reporting.filter_by_employee"}}
                  @items={{this.employeeItems}}
                  @selected={{@employeeId}}
                  @onChange={{this.selectedEmployeeDidChange}}
                  @searchable={{true}}
                  @onSearch={{this.onEmployeeSearch}}
                  @isLoading={{this.employeesData.isLoading}}
                  @clearable={{true}}
                  @disabled={{not @showIndividualApplications}}
                  @size="sm"
                  @placeholder="Search employees…"
                  @noResultsText="Type to search employees"
                />
              {{/if}}
              <ReSwitch
                @label={{t "common.reporting.terminated_employees"}}
                @checked={{@terminatedEmployees}}
                @onChange={{this.terminatedEmployeesDidChange}}
                @size="sm"
              />
              <ReSwitch
                @label={{t "common.reporting.exclude_term_verified"}}
                @checked={{@excludeTermVerified}}
                @onChange={{this.excludeTermVerifiedDidChange}}
                @size="sm"
              />
            </div>
          </div>
          {{#if @excludeNonVoucherPrograms}}
            <div class="flex flex-col gap-y-3">
              <ReCheckbox
                @label="Voucher Programs Only"
                @checked={{or @excludeNonVoucherPrograms false}}
                @onChange={{this.excludeNonVoucherProgramsDidChange}}
                @size="sm"
              />
            </div>
          {{/if}}
          <div class="flex flex-col gap-y-3">
            <ReCheckbox
              @label="Course Cash Programs Only"
              @checked={{or @courseCashProgramsOnly false}}
              @onChange={{this.courseCashProgramsOnlyDidChange}}
              @size="sm"
            />
          </div>
        </div>
      </fieldset>
    </div>
  </template>
}
